import React, { useEffect, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { SentenceParserConfig } from '../../../content/types';
export function SentenceParserRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.sentenceParser || question.data || {}) as SentenceParserConfig;
  const sentence = config.sentence || '';
  const words = config.words || [];
  const labels = config.labels || [];
  const correctAssignments = config.correctAssignments || {};
  const colors = config.colors || {};
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setAssignments({});
    setSelectedLabel(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    // Ready when all required words have a label
    const requiredWords = Object.keys(correctAssignments);
    const allAssigned = requiredWords.every(wordIndex => assignments[wordIndex]);
    onReadyChange?.(allAssigned && requiredWords.length > 0);
  }, [assignments, correctAssignments, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(assignments));
      setSelectedLabel(null);
    }
  }, [shouldValidate, assignments, onAnswer]);
  const handleWordClick = (wordIndex: number) => {
    if (shouldValidate) return;
    if (selectedLabel) {
      setAssignments(prev => ({
        ...prev,
        [wordIndex]: selectedLabel
      }));
    } else if (assignments[wordIndex]) {
      // Remove assignment if no label is selected
      const next = {
        ...assignments
      };
      delete next[wordIndex];
      setAssignments(next);
    }
  };
  const handleLabelClick = (label: string) => {
    if (shouldValidate) return;
    setSelectedLabel(selectedLabel === label ? null : label);
  };
  const getFullLabelName = (abbr: string) => {
    const map: Record<string, string> = {
      OND: 'Onderwerp (OND)',
      PV: 'Persoonsvorm (PV)',
      LV: 'Lijdend voorwerp (LV)',
      MV: 'Meewerkend voorwerp (MV)',
      BWB: 'Bepaling (BWB)',
      GEZ: 'Gezegde (GEZ)'
    };
    return map[abbr] || abbr;
  };
  if (words.length === 0 || labels.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-7537">
        <p className="text-red-700 font-bold" data-id="element-7538">
          ⚠️ Configuratie ontbreekt (words/labels)
        </p>
      </div>;
  }
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-6" data-id="element-7539">
      {/* Labels Pool */}
      <div className="bg-slate-50 rounded-2xl p-5 border-2 border-slate-200 shadow-inner" data-id="element-7540">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 text-center" data-id="element-7541">
          Kies een label, tik dan op het juiste woord(groep)
        </h3>
        <div className="flex flex-wrap justify-center gap-3" data-id="element-7542">
          {labels.map(label => {
          const isSelected = selectedLabel === label;
          const customColor = colors[label];
          let buttonStyle = {};
          let buttonClass = `px-4 py-2 rounded-xl font-bold text-base transition-all shadow-sm `;
          if (shouldValidate) {
            buttonClass += 'opacity-50 cursor-not-allowed bg-white text-slate-700 border-2 border-slate-200';
          } else if (isSelected) {
            if (customColor) {
              buttonStyle = {
                backgroundColor: customColor,
                color: 'white',
                borderColor: customColor
              };
              buttonClass += 'ring-4 ring-opacity-30 scale-105 border-2';
            } else {
              buttonClass += 'bg-indigo-500 text-white shadow-indigo-200 ring-4 ring-indigo-200 scale-105 border-2 border-indigo-500';
            }
          } else {
            buttonClass += 'bg-white text-slate-700 border-2 border-slate-200 hover:border-slate-300 hover:-translate-y-1 hover:shadow-md';
          }
          return <motion.button key={label} whileTap={shouldValidate ? {} : {
            scale: 0.95
          }} onClick={() => handleLabelClick(label)} disabled={shouldValidate} className={buttonClass} style={buttonStyle} aria-pressed={isSelected} data-id="element-7543">
                {getFullLabelName(label)}
              </motion.button>;
        })}
        </div>
      </div>

      {/* Sentence Area */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-slate-200 shadow-sm flex flex-wrap gap-x-2 sm:gap-x-3 gap-y-10 justify-center items-end min-h-[120px]" data-id="element-7544">
        {words.map((word, index) => {
        const assignedLabel = assignments[index];
        const isTargetable = selectedLabel !== null && !shouldValidate;
        const customColor = assignedLabel ? colors[assignedLabel] : undefined;
        let validationClass = 'border-transparent';
        let ValidationIcon = null;
        if (shouldValidate && assignedLabel) {
          const isCorrect = assignedLabel === correctAssignments[index];
          validationClass = isCorrect ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-red-500 bg-red-50 text-red-800';
          ValidationIcon = isCorrect ? <CheckCircle2 className="w-4 h-4 text-emerald-500 absolute -top-2 -right-2 bg-white rounded-full" data-id="element-7545" /> : <XCircle className="w-4 h-4 text-red-500 absolute -top-2 -right-2 bg-white rounded-full" data-id="element-7546" />;
        }
        let badgeStyle = {};
        let badgeClass = `absolute -top-8 px-2 py-1 rounded-lg text-xs sm:text-sm font-bold border-2 shadow-sm whitespace-nowrap z-10 `;
        if (shouldValidate) {
          badgeClass += validationClass;
        } else if (customColor) {
          badgeStyle = {
            backgroundColor: `${customColor}20`,
            borderColor: customColor,
            color: customColor
          };
        } else {
          badgeClass += 'bg-indigo-100 border-indigo-200 text-indigo-800';
        }
        let wordStyle = {};
        let wordClass = `text-xl sm:text-2xl font-medium px-2 py-1 rounded-lg transition-colors relative `;
        if (shouldValidate) {
          wordClass += 'cursor-default ';
        } else if (isTargetable) {
          wordClass += 'cursor-pointer hover:bg-slate-100 ';
        } else {
          wordClass += 'cursor-pointer hover:bg-slate-50 ';
        }
        if (assignedLabel && !shouldValidate) {
          if (customColor) {
            wordStyle = {
              backgroundColor: `${customColor}15`,
              color: customColor,
              borderBottomColor: customColor
            };
            wordClass += 'border-b-2 ';
          } else {
            wordClass += 'bg-indigo-50/50 text-indigo-900 border-b-2 border-indigo-300 ';
          }
        } else {
          wordClass += 'border-b-2 border-transparent ';
        }
        return <div key={index} className="relative flex flex-col items-center group" data-id="element-7547">
              <AnimatePresence data-id="element-7548">
                {assignedLabel && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} className={badgeClass} style={badgeStyle} data-id="element-7549">
                    {assignedLabel}
                    {ValidationIcon}
                  </motion.div>}
              </AnimatePresence>

              <button onClick={() => handleWordClick(index)} disabled={shouldValidate} className={wordClass} style={wordStyle} data-id="element-7550">
                {word}
              </button>
            </div>;
      })}
      </div>
    </div>;
}